import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  FileText, Save, ArrowLeft, Loader2, ChevronDown, Clock, 
  User, Calendar, CheckCircle2, Briefcase, DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SESCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const poNumberParam = searchParams.get('po');

  const [loading, setLoading] = useState(false);
  const [loadingPOs, setLoadingPOs] = useState(true);
  const [loadingPOData, setLoadingPOData] = useState(false);

  // Master data
  const [servicePOList, setServicePOList] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);

  // Selected PO data
  const [selectedPO, setSelectedPO] = useState(poNumberParam || '');
  const [poData, setPoData] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    posting_date: new Date().toISOString().split('T')[0],
    service_period_start: new Date().toISOString().split('T')[0],
    service_period_end: new Date().toISOString().split('T')[0],
    external_reference: '',
    header_text: '',
    requires_acceptance: true,
    created_by: 'ADMIN'
  });

  // Service Entry Items
  const [items, setItems] = useState([]);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  // Load initial data
  useEffect(() => {
    loadMasterData();
  }, []);

  // Load PO data when selected
  useEffect(() => {
    if (selectedPO) {
      loadServicePO(selectedPO);
    }
  }, [selectedPO]);

  const loadMasterData = async () => {
    try {
      setLoadingPOs(true);
      const [posRes, ccRes, glRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/service-entries/service-pos`),
        axios.get(`${BACKEND_URL}/api/controlling/cost-centers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/gl-accounts`).catch(() => ({ data: [] }))
      ]);
      setServicePOList(posRes.data);
      setCostCenters(ccRes.data || []);
      setGlAccounts(glRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load Service POs');
    } finally {
      setLoadingPOs(false);
    }
  };

  const loadServicePO = async (poNumber) => {
    try {
      setLoadingPOData(true);
      const response = await axios.get(`${BACKEND_URL}/api/purchase-orders/${poNumber}`);
      const po = response.data;
      setPoData(po);
      
      // Pre-fill form with PO data
      setFormData(prev => ({
        ...prev,
        header_text: `Service Entry for PO ${po.po_number}`
      }));
      
      // Filter to service items only and create entry items
      const serviceItems = po.items
        .filter(item => 
          item.item_category === 'D' || 
          item.goods_receipt_indicator === false ||
          item.service_indicator === true ||
          !item.goods_receipt_indicator  // Default for service
        )
        .filter(item => {
          const ordered = parseFloat(item.po_quantity || item.quantity || 0);
          const entered = parseFloat(item.quantity_entered || 0);
          return ordered > entered;  // Has open quantity
        })
        .map((item, idx) => {
          const ordered = parseFloat(item.po_quantity || item.quantity || 0);
          const entered = parseFloat(item.quantity_entered || 0);
          const openQty = ordered - entered;
          const price = parseFloat(item.net_price || 0) / parseInt(item.price_unit || 1);
          
          return {
            item_number: (idx + 1) * 10,
            selected: true,
            po_number: po.po_number,
            po_item: item.item_number,
            service_number: item.service_number || '',
            short_text: item.short_text || '',
            long_text: '',
            service_type: 'TIME_BASED',
            quantity: openQty,
            unit_of_measure: item.unit_of_measure || 'HR',
            gross_price: price,
            price_unit: 1,
            currency: item.currency || po.currency || 'USD',
            tax_code: item.tax_code || '',
            gl_account: item.gl_account || '',
            cost_center: item.cost_center || '',
            account_assignment_cat: item.account_assignment_cat || 'K',
            work_start_date: new Date().toISOString().split('T')[0],
            work_end_date: new Date().toISOString().split('T')[0],
            personnel_name: '',
            // Reference data
            po_quantity: ordered,
            po_price: price,
            quantity_already_entered: entered,
            open_quantity: openQty
          };
        });
      
      setItems(serviceItems);
      setActiveItemIndex(0);
      
      if (serviceItems.length === 0) {
        toast.error('No open service items found on this PO');
      } else {
        toast.success(`Loaded ${serviceItems.length} service item(s) from PO ${poNumber}`);
      }
    } catch (error) {
      console.error('Error loading PO:', error);
      toast.error('Failed to load Service PO');
    } finally {
      setLoadingPOData(false);
    }
  };

  const handlePOSelect = (poNumber) => {
    setSelectedPO(poNumber);
    if (!poNumber) {
      setPoData(null);
      setItems([]);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleItemSelect = (index, selected) => {
    const newItems = [...items];
    newItems[index].selected = selected;
    setItems(newItems);
  };

  const validateForm = () => {
    if (!selectedPO) {
      toast.error('Please select a Service PO');
      return false;
    }
    
    const selectedItems = items.filter(item => item.selected);
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return false;
    }
    
    for (const item of selectedItems) {
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`Item ${item.po_item}: Valid quantity is required`);
        return false;
      }
      if (!item.short_text) {
        toast.error(`Item ${item.po_item}: Service description is required`);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const selectedItems = items.filter(item => item.selected);
      
      const payload = {
        ...formData,
        vendor_id: poData.vendor_id,
        po_number: poData.po_number,
        items: selectedItems.map(item => ({
          item_number: item.item_number,
          po_number: item.po_number,
          po_item: item.po_item,
          service_number: item.service_number || null,
          short_text: item.short_text,
          long_text: item.long_text || null,
          service_type: item.service_type,
          quantity: parseFloat(item.quantity),
          unit_of_measure: item.unit_of_measure,
          gross_price: parseFloat(item.gross_price),
          price_unit: parseInt(item.price_unit) || 1,
          currency: item.currency,
          tax_code: item.tax_code || null,
          gl_account: item.gl_account || null,
          cost_center: item.cost_center || null,
          account_assignment_cat: item.account_assignment_cat || 'K',
          work_start_date: item.work_start_date || null,
          work_end_date: item.work_end_date || null,
          personnel_name: item.personnel_name || null
        }))
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/service-entries`, payload);
      toast.success(`Service Entry ${response.data.entry_sheet_number} created successfully!`);
      navigate(`/p2p/ses-view/${response.data.entry_sheet_number}/${response.data.fiscal_year}`);
    } catch (error) {
      console.error('Error creating service entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to create service entry');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const selectedItems = items.filter(item => item.selected);
  const totalValue = selectedItems.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.gross_price) || 0;
    return sum + (qty * price);
  }, 0);
  const totalHours = selectedItems.reduce((sum, item) => {
    if (item.unit_of_measure === 'HR' || item.unit_of_measure === 'H') {
      return sum + (parseFloat(item.quantity) || 0);
    }
    return sum;
  }, 0);

  const currentItem = items[activeItemIndex];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/p2p/ses-list')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Briefcase className="w-7 h-7 text-orange-600" />
                Create Service Entry Sheet (ML81N)
              </h1>
              <p className="text-sm text-gray-500">Record services performed against a Service PO</p>
            </div>
          </div>
        </div>

        {/* Service PO Selection */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Purchase Order</h2>
          <div className="flex gap-4 items-end">
            <div className="flex-1 max-w-lg">
              <Label>Select Service PO *</Label>
              <div className="relative">
                <select
                  value={selectedPO}
                  onChange={(e) => handlePOSelect(e.target.value)}
                  disabled={loadingPOs || loadingPOData}
                  className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm appearance-none bg-white focus:ring-2 focus:ring-orange-500 pr-10"
                >
                  <option value="">-- Select a Service PO --</option>
                  {servicePOList.map((po) => (
                    <option key={po.po_number} value={po.po_number}>
                      {po.po_number} | {po.vendor_name || po.vendor_id} | Open: {po.open_for_entry?.toFixed(2)} | {po.currency} {po.total_value?.toLocaleString()}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-0.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {loadingPOs && <p className="text-xs text-gray-500 mt-1">Loading Service POs...</p>}
            </div>
            {loadingPOData && (
              <div className="flex items-center text-orange-600">
                <Loader2 className="animate-spin w-5 h-5 mr-2" />
                Loading PO Details...
              </div>
            )}
          </div>
          
          {servicePOList.length === 0 && !loadingPOs && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                No Service POs available. Create a PO with service items (Item Category D) first.
              </p>
            </div>
          )}
        </div>

        {/* Show form only when PO is selected */}
        {poData && items.length > 0 && (
          <form onSubmit={handleSubmit}>
            {/* PO Summary */}
            <div className="bg-gradient-to-r from-orange-50 to-pink-50 rounded-lg border border-orange-200 p-4 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-xs text-orange-600">Vendor</p>
                  <p className="font-semibold text-orange-900">{poData.vendor_id} - {poData.vendor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Company Code</p>
                  <p className="font-semibold text-orange-900">{poData.company_code}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Currency</p>
                  <p className="font-semibold text-orange-900">{poData.currency}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">PO Status</p>
                  <p className="font-semibold text-orange-900">{poData.status}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Service Items</p>
                  <p className="font-semibold text-green-700">{items.length} item(s)</p>
                </div>
              </div>
            </div>

            {/* Entry Header */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Entry Header</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Entry Date *</Label>
                  <Input
                    type="date"
                    value={formData.entry_date}
                    onChange={(e) => setFormData({...formData, entry_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Posting Date *</Label>
                  <Input
                    type="date"
                    value={formData.posting_date}
                    onChange={(e) => setFormData({...formData, posting_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Service Period Start</Label>
                  <Input
                    type="date"
                    value={formData.service_period_start}
                    onChange={(e) => setFormData({...formData, service_period_start: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Service Period End</Label>
                  <Input
                    type="date"
                    value={formData.service_period_end}
                    onChange={(e) => setFormData({...formData, service_period_end: e.target.value})}
                  />
                </div>
                <div>
                  <Label>External Reference</Label>
                  <Input
                    value={formData.external_reference}
                    onChange={(e) => setFormData({...formData, external_reference: e.target.value})}
                    placeholder="Vendor's sheet number"
                    maxLength={16}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Header Text</Label>
                  <Input
                    value={formData.header_text}
                    onChange={(e) => setFormData({...formData, header_text: e.target.value})}
                    placeholder="Notes about the service..."
                    maxLength={200}
                  />
                </div>
                <div className="flex items-center">
                  <Checkbox
                    checked={formData.requires_acceptance}
                    onCheckedChange={(checked) => setFormData({...formData, requires_acceptance: checked})}
                  />
                  <Label className="ml-2 text-sm">Requires Acceptance</Label>
                </div>
              </div>
            </div>

            {/* Service Entry Items */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Service Entry Items</h2>
                <div className="text-sm text-gray-500">
                  {selectedItems.length} of {items.length} items selected
                </div>
              </div>

              {/* Item Selector */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1 max-w-md">
                  <Label className="text-sm text-gray-600">Select Item to Edit</Label>
                  <div className="relative">
                    <select
                      value={activeItemIndex}
                      onChange={(e) => setActiveItemIndex(parseInt(e.target.value))}
                      className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm appearance-none bg-white pr-10"
                    >
                      {items.map((item, index) => (
                        <option key={`ses-item-${item.po_item || index}`} value={index}>
                          Item {item.po_item} | {item.short_text?.substring(0, 30)}... | Open: {item.open_quantity?.toFixed(2)} {item.unit_of_measure}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-0.5 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems(items.map(i => ({...i, selected: true})))}>
                    Select All
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems(items.map(i => ({...i, selected: false})))}>
                    Deselect All
                  </Button>
                </div>
              </div>

              {/* Current Item Details */}
              {currentItem && (
                <div className="border-t pt-4">
                  {/* Item Selection Checkbox */}
                  <div className="flex items-center space-x-3 mb-4 p-3 bg-orange-50 rounded-md">
                    <Checkbox
                      checked={currentItem.selected}
                      onCheckedChange={(checked) => handleItemSelect(activeItemIndex, checked)}
                    />
                    <Label className="text-sm font-medium">
                      Include this service (PO Item {currentItem.po_item})
                    </Label>
                  </div>

                  {/* Quantities Reference */}
                  <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-md">
                    <div>
                      <Label className="text-xs text-gray-500">PO Quantity</Label>
                      <p className="font-semibold text-gray-900">{currentItem.po_quantity?.toFixed(2)} {currentItem.unit_of_measure}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Already Entered</Label>
                      <p className="font-semibold text-orange-700">{currentItem.quantity_already_entered?.toFixed(2)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Open for Entry</Label>
                      <p className="font-semibold text-green-700">{currentItem.open_quantity?.toFixed(2)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">PO Price</Label>
                      <p className="font-semibold text-gray-900">{currentItem.currency} {currentItem.po_price?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Service Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                      <Label>Service Description *</Label>
                      <Input
                        value={currentItem.short_text}
                        onChange={(e) => handleItemChange(activeItemIndex, 'short_text', e.target.value)}
                        placeholder="Brief description of service"
                        maxLength={40}
                        required
                      />
                    </div>
                    <div>
                      <Label>Service Type</Label>
                      <select
                        value={currentItem.service_type}
                        onChange={(e) => handleItemChange(activeItemIndex, 'service_type', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="TIME_BASED">Time-Based (Hours/Days)</option>
                        <option value="LUMP_SUM">Lump Sum (Fixed)</option>
                        <option value="UNIT_BASED">Unit-Based</option>
                      </select>
                    </div>
                    <div>
                      <Label>Unit of Measure</Label>
                      <select
                        value={currentItem.unit_of_measure}
                        onChange={(e) => handleItemChange(activeItemIndex, 'unit_of_measure', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="HR">HR - Hours</option>
                        <option value="DA">DA - Days</option>
                        <option value="WK">WK - Weeks</option>
                        <option value="MO">MO - Months</option>
                        <option value="EA">EA - Each</option>
                        <option value="LS">LS - Lump Sum</option>
                      </select>
                    </div>
                    <div>
                      <Label>Quantity *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentItem.quantity}
                        onChange={(e) => handleItemChange(activeItemIndex, 'quantity', e.target.value)}
                        className={parseFloat(currentItem.quantity) > currentItem.open_quantity ? 'border-red-500' : ''}
                      />
                      {parseFloat(currentItem.quantity) > currentItem.open_quantity && (
                        <p className="text-xs text-red-500 mt-1">Exceeds open quantity!</p>
                      )}
                    </div>
                    <div>
                      <Label>Gross Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentItem.gross_price}
                        onChange={(e) => handleItemChange(activeItemIndex, 'gross_price', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Work Start Date</Label>
                      <Input
                        type="date"
                        value={currentItem.work_start_date}
                        onChange={(e) => handleItemChange(activeItemIndex, 'work_start_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Work End Date</Label>
                      <Input
                        type="date"
                        value={currentItem.work_end_date}
                        onChange={(e) => handleItemChange(activeItemIndex, 'work_end_date', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Personnel Name</Label>
                      <Input
                        value={currentItem.personnel_name}
                        onChange={(e) => handleItemChange(activeItemIndex, 'personnel_name', e.target.value)}
                        placeholder="Service provider name"
                      />
                    </div>
                    <div>
                      <Label>Cost Center</Label>
                      <select
                        value={currentItem.cost_center}
                        onChange={(e) => handleItemChange(activeItemIndex, 'cost_center', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="">-- Select --</option>
                        {currentItem.cost_center && !costCenters.find(c => c.cost_center_id === currentItem.cost_center) && (
                          <option value={currentItem.cost_center}>{currentItem.cost_center} (From PO)</option>
                        )}
                        {costCenters.map(cc => (
                          <option key={cc.cost_center_id} value={cc.cost_center_id}>
                            {cc.cost_center_id} - {cc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>G/L Account</Label>
                      <select
                        value={currentItem.gl_account}
                        onChange={(e) => handleItemChange(activeItemIndex, 'gl_account', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="">-- Select --</option>
                        {currentItem.gl_account && !glAccounts.find(g => g.account_number === currentItem.gl_account) && (
                          <option value={currentItem.gl_account}>{currentItem.gl_account} (From PO)</option>
                        )}
                        {glAccounts.map(gl => (
                          <option key={gl.account_number} value={gl.account_number}>
                            {gl.account_number} - {gl.account_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Long Text */}
                  <div className="mt-4">
                    <Label>Detailed Description / Work Log</Label>
                    <textarea
                      value={currentItem.long_text || ''}
                      onChange={(e) => handleItemChange(activeItemIndex, 'long_text', e.target.value)}
                      placeholder="Detailed description of work performed, activities, deliverables..."
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm h-24"
                      maxLength={500}
                    />
                  </div>

                  {/* Item Value */}
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700">Item Value</span>
                      <span className="font-bold text-green-900">
                        {currentItem.currency} {(parseFloat(currentItem.quantity || 0) * parseFloat(currentItem.gross_price || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Entry Totals */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Entry Totals</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Items Selected</p>
                  <p className="text-xl font-bold text-gray-900">{selectedItems.length}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs text-orange-600 flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3" /> Total Hours
                  </p>
                  <p className="text-xl font-bold text-orange-900">{totalHours.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xs text-green-600 flex items-center justify-center gap-1">
                    <DollarSign className="w-3 h-3" /> Total Value
                  </p>
                  <p className="text-xl font-bold text-green-900">{poData?.currency} {totalValue.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Status</p>
                  <p className="text-xl font-bold text-orange-900">Draft</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/p2p/ses-list')}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || selectedItems.length === 0} className="bg-orange-600 hover:bg-orange-700">
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Create Entry Sheet</>
                )}
              </Button>
            </div>
          </form>
        )}

        {/* Empty state */}
        {!poData && !loadingPOData && (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Service PO</h3>
            <p className="text-gray-600">Choose a Service PO from the dropdown to start recording services</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SESCreate;
